import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sunrise, Menu, X, Phone } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { BUSINESS } from "../data/content";

export const Navbar = () => {
  const { lang, toggle, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const links = [
    { to: "/", label: t.nav.home, testid: "nav-home-link" },
    { to: "/about", label: t.nav.about, testid: "nav-about-link" },
    { to: "/fabrics", label: t.nav.fabrics, testid: "nav-fabrics-link" },
    { to: "/contact", label: t.nav.contact, testid: "nav-contact-link" },
  ];

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-black/5 bg-white/70 backdrop-blur-xl" data-testid="navbar">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 sm:px-8">
        <Link to="/" className="group flex items-center gap-3" data-testid="nav-logo-link" onClick={() => setOpen(false)}>
          <span className="grid h-11 w-11 place-items-center rounded-full bg-maroon text-gold transition-transform duration-500 group-hover:rotate-45">
            <Sunrise size={20} strokeWidth={1.8} />
          </span>
          <span className="leading-tight">
            <span className="block text-[10px] font-semibold uppercase tracking-[0.35em] text-gold">HB Group</span>
            <span className={`block text-base font-semibold text-maroon ${lang === "hi" ? "font-hindi" : "font-display"}`}>
              {lang === "hi" ? BUSINESS.nameHi : BUSINESS.nameEn}
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-8 md:flex" data-testid="nav-desktop-menu">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) =>
                `relative text-sm font-medium tracking-wide transition-colors duration-300 hover:text-maroon ${
                  isActive ? "text-maroon" : "text-ink/70"
                } ${lang === "hi" ? "font-hindi" : ""}`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  <span
                    className={`absolute -bottom-1.5 left-0 h-px bg-gold transition-all duration-500 ${
                      isActive ? "w-full" : "w-0"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={`tel:${BUSINESS.phones[2]}`}
            className="hidden items-center gap-2 rounded-full border border-gold/50 px-4 py-2 text-xs font-semibold tracking-wider text-maroon transition-colors duration-300 hover:bg-maroon hover:text-ivory lg:flex"
            data-testid="nav-call-button"
          >
            <Phone size={14} /> {BUSINESS.phones[2]}
          </a>
          <button
            onClick={toggle}
            className="flex items-center rounded-full border border-black/10 bg-white/80 p-1 text-xs font-semibold"
            data-testid="language-toggle-button"
            aria-label="Toggle language"
          >
            <span className={`rounded-full px-3 py-1.5 transition-colors duration-300 ${lang === "en" ? "bg-maroon text-ivory" : "text-ink/60"}`}>EN</span>
            <span className={`rounded-full px-3 py-1.5 font-hindi transition-colors duration-300 ${lang === "hi" ? "bg-maroon text-ivory" : "text-ink/60"}`}>हिं</span>
          </button>
          <button
            className="grid h-10 w-10 place-items-center rounded-full border border-black/10 text-maroon md:hidden"
            onClick={() => setOpen((o) => !o)}
            data-testid="nav-mobile-menu-button"
            aria-label="Menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            key={location.pathname}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-black/5 bg-white/90 backdrop-blur-xl md:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-${l.testid}`}
                  className={({ isActive }) =>
                    `rounded-lg px-3 py-3 text-base font-medium transition-colors duration-300 ${
                      isActive ? "bg-maroon/5 text-maroon" : "text-ink/70"
                    } ${lang === "hi" ? "font-hindi" : ""}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
